import React, { useState, useEffect } from "react";
import AdminSidebar from "./AdminSidebar";
import AdminTopBar from "./AdminTopBar";
import api from "./api/axios";
import { useActivityLogger } from "./hooks/useActivity";
import { useSearch } from "./contexts/SearchContext";

export default function AdminCaregiverPatient() {
  const { searchQuery } = useSearch();
  const [relations, setRelations] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    caregiver_id: "",
    patient_id: "",
  });

  const { logAdminActivity } = useActivityLogger();

  // Fetch all data
  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [caregiversRes, patientsRes] = await Promise.all([
        api.get("/caregivers"),
        api.get("/patients"),
      ]);
      setCaregivers(caregiversRes.data);
      setPatients(patientsRes.data);
    } catch (err) {
      console.error("Gagal mengambil data", err);
      alert("Gagal memuat data: " + (err.response?.data?.error || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch caregiver-patient relations - SINGLE API CALL
  const fetchRelations = async () => {
    try {
      const res = await api.get("/admins/caregiver-patient/all");
      setRelations(res.data || []);
    } catch (err) {
      console.error("Gagal mengambil relasi", err);
    }
  };

  useEffect(() => {
    fetchData();
    fetchRelations();
    logAdminActivity(
      "view",
      "view_admin_caregiver_patient",
      "caregiver_patient",
      null,
      "admin",
      "success",
      "",
      "Admin melihat manajemen caregiver-patient"
    );
  }, []);

  // useEffect(() => {
  //   if (caregivers.length > 0) {
  //     fetchRelations();
  //   }
  // }, [caregivers]);

  // Filter relations based on search
  const filteredRelations = relations.filter(
    (rel) =>
      rel.caregiver.nama_ktp
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      rel.patient.nama_ktp?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle Delete
  const handleDelete = async (
    caregiverId,
    patientId,
    caregiverName,
    patientName
  ) => {
    if (
      window.confirm(
        `Yakin ingin menghapus relasi antara "${caregiverName}" dan "${patientName}"?`
      )
    ) {
      try {
        // PERBAIKAN: Pastikan Content-Type header di-set dengan benar
        await api.delete("/admins/caregiver-patient", {
          data: {
            caregiver_id: parseInt(caregiverId),
            patient_id: parseInt(patientId),
          },
          headers: {
            "Content-Type": "application/json",
          },
        });

        logAdminActivity(
          "delete",
          "remove_patient_from_caregiver",
          "caregiver_patient",
          null,
          `${caregiverName} - ${patientName}`,
          "success",
          "",
          "Admin menghapus relasi caregiver-patient"
        );
        alert("Relasi berhasil dihapus");
        await fetchRelations();
      } catch (err) {
        console.error("Error detail:", err.response?.data || err);
        alert(
          "Gagal menghapus relasi: " +
            (err.response?.data?.error || err.message)
        );
      }
    }
  };

  // Handle Submit (Create)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // PERBAIKAN: Parse integer dan pastikan data valid
      const requestData = {
        caregiver_id: parseInt(formData.caregiver_id),
        patient_id: parseInt(formData.patient_id),
      };

      // Validasi data sebelum mengirim
      if (!requestData.caregiver_id || !requestData.patient_id) {
        alert("Mohon pilih caregiver dan patient");
        return;
      }

      if (requestData.caregiver_id === requestData.patient_id) {
        alert("Caregiver dan patient tidak boleh sama");
        return;
      }

      await api.post("/admins/caregiver-patient", requestData);

      logAdminActivity(
        "create",
        "assign_patient_to_caregiver",
        "caregiver_patient",
        null,
        `Caregiver ${requestData.caregiver_id} - Patient ${requestData.patient_id}`,
        "success",
        "",
        "Admin menambahkan pasien ke pendamping"
      );
      alert("Pasien berhasil ditambahkan ke pendamping");
      closeModal();
      await fetchRelations();
    } catch (err) {
      console.error("Error detail:", err.response?.data || err);
      alert(
        "Gagal menambahkan pasien: " +
          (err.response?.data?.error || err.message)
      );
    }
  };
  // Modal Helpers
  const openAddModal = () => {
    setFormData({ caregiver_id: "", patient_id: "" });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({ caregiver_id: "", patient_id: "" });
  };

  return (
    <div className="min-h-screen flex">
      <AdminSidebar />
      <div className="flex-1 p-6 bg-gray-50">
        <AdminTopBar />

        <div className="bg-white p-4 rounded shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              Manajemen Relasi Caregiver-Patient
            </h2>
            <button
              onClick={openAddModal}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              + Tambah Relasi
            </button>
          </div>

          <table className="w-full text-sm">
            <thead className="border-b">
              <tr>
                <th className="py-2 text-left">Pendamping (Caregiver)</th>
                <th className="py-2 text-left">Pasien (Patient)</th>
                <th className="py-2 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="3" className="py-4 text-center">
                    Loading...
                  </td>
                </tr>
              ) : filteredRelations.length === 0 ? (
                <tr>
                  <td colSpan="3" className="py-4 text-center">
                    Tidak ada relasi ditemukan.
                  </td>
                </tr>
              ) : (
                filteredRelations.map((rel, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="py-3">
                      {rel.caregiver.nama_ktp} (ID: {rel.caregiver.ID})
                    </td>
                    <td className="py-3">
                      {rel.patient.nama_ktp} (ID: {rel.patient.ID})
                    </td>
                    <td className="py-3 text-right">
                      <button
                        onClick={() =>
                          handleDelete(
                            rel.caregiver.ID,
                            rel.patient.ID,
                            rel.caregiver.nama_ktp,
                            rel.patient.nama_ktp
                          )
                        }
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <img
                          src="/icon-trash.png"
                          alt="Delete"
                          className="h-5 w-5"
                        />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Popup for Add */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h3 className="text-lg font-bold mb-4">Tambah Relasi Baru</h3>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Pendamping (Caregiver)
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={formData.caregiver_id}
                  onChange={(e) =>
                    setFormData({ ...formData, caregiver_id: e.target.value })
                  }
                  required
                >
                  <option value="">Pilih Pendamping</option>
                  {caregivers.map((cg) => (
                    <option key={cg.ID} value={cg.ID}>
                      {cg.nama_ktp} (ID: {cg.ID})
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Pasien (Patient)
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={formData.patient_id}
                  onChange={(e) =>
                    setFormData({ ...formData, patient_id: e.target.value })
                  }
                  required
                >
                  <option value="">Pilih Pasien</option>
                  {patients.map((pt) => (
                    <option key={pt.ID} value={pt.ID}>
                      {pt.nama_ktp} (ID: {pt.ID})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Simpan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
